import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  User, Plus, Trash2, AlertCircle,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Field, Input, Select, Textarea, Button, Spinner, Pill,
} from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// ProjectIndividualAssignmentsCard — embed on ProjectDetailScreen.
// T-131. Sits next to ProjectCrewAssignmentsCard. Uses the existing
// `project_team` table (one row per employee assignment, with role +
// dates). Manager+ can add / end assignments inline. Read-only for
// everyone else.
//
// Use case: ad-hoc / labour-hire / one-off project work where standing up
// a whole crew is overkill. Project Manager + Supervisor + Site Lead are
// also stored on the projects row directly; this card surfaces ALL
// project_team rows so the picture is unified.
// =============================================================================

const ROLES_ON_PROJECT = [
  "PM", "Supervisor", "PICW", "Site Lead", "Tech", "Rigger", "Driver",
  "Apprentice", "Labour hire", "Subcontractor", "Other",
] as const;
type RoleOnProject = typeof ROLES_ON_PROJECT[number] | string;

export interface Props {
  projectId: string;
  projectScheduledEnd: string | null;
}

interface AssignmentRow {
  assignment_id:   string;
  employee_id:     string;
  role_on_project: string;
  assigned_from:   string;
  assigned_to:     string | null;
  allocation_pct:  number | null;
  notes:           string | null;
  employees: {
    employee_id: string;
    first_name:  string;
    last_name:   string;
    primary_role_name: string | null;
  } | null;
}

interface EmployeeOption {
  employee_id:       string;
  first_name:        string;
  last_name:         string;
  primary_role_name: string | null;
}

// -----------------------------------------------------------------------------

export default function ProjectIndividualAssignmentsCard({
  projectId, projectScheduledEnd,
}: Props) {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data: assignments, isLoading, refetch } = useQuery({
    queryKey: ["project_team_individual", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_team")
        .select(`
          assignment_id, employee_id, role_on_project, assigned_from,
          assigned_to, allocation_pct, notes,
          employees:employee_id (employee_id, first_name, last_name, primary_role_name)
        `)
        .eq("project_id", projectId)
        .order("assigned_from", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as AssignmentRow[];
    },
  });

  async function removeAssignment(assignmentId: string) {
    if (!window.confirm("Remove this person from the project?")) return;
    const { error } = await supabase
      .from("project_team")
      .delete()
      .eq("assignment_id", assignmentId);
    if (error) { window.alert(`Couldn't remove: ${error.message}`); return; }
    refetch();
    qc.invalidateQueries({ queryKey: ["project", projectId] });
  }

  return (
    <Card>
      <div className="p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <User size={14} className="text-text-muted"/>
            <span className="text-[12px] uppercase tracking-wider text-text-muted">
              Individuals on this project
            </span>
          </div>
          {canManage && !adding && (
            <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(true)}>
              Assign person
            </Button>
          )}
        </div>

        {isLoading && <div className="flex justify-center py-3"><Spinner size={14}/></div>}

        {!isLoading && (assignments ?? []).length === 0 && !adding && (
          <div className="text-[12px] text-text-muted py-2">
            No individuals assigned yet. Crews live on the card above; this is for
            ad-hoc / labour-hire / one-off assignments outside of a crew.
            {canManage ? " Use Assign person to add one." : ""}
          </div>
        )}

        {!isLoading && (assignments ?? []).length > 0 && (
          <div className="space-y-1.5">
            {assignments!.map(a => {
              const _today = new Date().toISOString().slice(0, 10);
              const _ended = a.assigned_to && a.assigned_to < _today;
              const _upcoming = a.assigned_from > _today;
              const name = a.employees
                ? `${a.employees.last_name}, ${a.employees.first_name}`
                : a.employee_id;
              return (
                <div key={a.assignment_id} className="flex items-center justify-between gap-2 bg-ink rounded-md px-3 py-2 border border-line/50">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-accent/12 text-accent flex items-center justify-center flex-shrink-0">
                      <User size={13}/>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12px]">
                        <span className="font-medium">{name}</span>
                        <span className="text-text-muted ml-2">· {a.role_on_project}</span>
                        {a.allocation_pct != null && (
                          <span className="text-text-muted ml-1.5">({a.allocation_pct}%)</span>
                        )}
                      </div>
                      <div className="text-[10px] text-text-muted">
                        {fmtDate(a.assigned_from)} → {a.assigned_to ? fmtDate(a.assigned_to) : "open"}
                        {a.notes && <span className="ml-2 italic">· {a.notes}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {_ended
                      ? <Pill label="Past" tone="closed" small/>
                      : _upcoming
                        ? <Pill label="Upcoming" tone="pending" small/>
                        : <Pill label="Current" tone="ok" small/>}
                    {canManage && (
                      <button
                        type="button"
                        onClick={() => removeAssignment(a.assignment_id)}
                        className="text-[10px] text-text-muted hover:text-lost p-1 rounded hover:bg-lost/10"
                        title="Remove this person"
                      >
                        <Trash2 size={11}/>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {adding && canManage && (
          <AddAssignmentForm
            projectId={projectId}
            projectScheduledEnd={projectScheduledEnd}
            onClose={() => { setAdding(false); refetch(); }}
          />
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------

function AddAssignmentForm({
  projectId, projectScheduledEnd, onClose,
}: {
  projectId: string;
  projectScheduledEnd: string | null;
  onClose: () => void;
}) {
  const [employeeId, setEmployeeId]   = useState("");
  const [role, setRole]               = useState<string>("Tech");
  const [from, setFrom]               = useState(new Date().toISOString().slice(0, 10));
  const [to,   setTo]                 = useState(projectScheduledEnd ?? "");
  const [allocation, setAllocation]   = useState<string>("");
  const [notes, setNotes]             = useState("");
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  const { data: employees } = useQuery({
    queryKey: ["employees_for_project_assign"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, primary_role_name")
        .eq("is_active", true)
        .order("last_name");
      if (error) throw error;
      return (data ?? []) as EmployeeOption[];
    },
  });

  async function save() {
    if (!employeeId || !role || !from) return;
    setSaving(true); setError(null);
    try {
      const { error } = await supabase.from("project_team").insert({
        project_id:      projectId,
        employee_id:     employeeId,
        role_on_project: role,
        assigned_from:   from,
        assigned_to:     to || null,
        allocation_pct:  allocation.trim() ? parseInt(allocation, 10) : null,
        notes:           notes.trim() || null,
      });
      if (error) throw error;
      onClose();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 border-t border-line pt-3 space-y-2.5">
      <div className="text-[11px] uppercase tracking-wider text-text-muted">Assign individual</div>

      <div className="grid md:grid-cols-2 gap-2">
        <Field label="Employee">
          <Select value={employeeId} onChange={e => setEmployeeId(e.target.value)}>
            <option value="">— pick a person —</option>
            {(employees ?? []).map(em => (
              <option key={em.employee_id} value={em.employee_id}>
                {em.last_name}, {em.first_name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Role on project">
          <Select value={role} onChange={e => setRole(e.target.value)}>
            {ROLES_ON_PROJECT.map(r => <option key={r} value={r}>{r}</option>)}
          </Select>
        </Field>
      </div>

      <div className="grid md:grid-cols-3 gap-2">
        <Field label="From">
          <Input type="date" value={from} onChange={e => setFrom(e.target.value)}/>
        </Field>
        <Field label="To" hint="Blank = open-ended">
          <Input type="date" value={to} onChange={e => setTo(e.target.value)}/>
        </Field>
        <Field label="Allocation %" hint="Optional — fraction of their time.">
          <Input
            type="number"
            min="0"
            max="100"
            mono
            value={allocation}
            onChange={e => setAllocation(e.target.value)}
            placeholder="e.g. 50"
          />
        </Field>
      </div>

      <Field label="Notes">
        <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional"/>
      </Field>

      {error && (
        <div className="text-[11px] text-lost inline-flex items-center gap-1">
          <AlertCircle size={11}/> {error}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!employeeId || !role || !from || saving} onClick={save}>
          {saving ? <Spinner size={12}/> : "Add to team"}
        </Button>
        <Button size="sm" variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}
